import { useEffect, useState, useRef } from "react";
import { useRealtime } from "../utils/RealtimeProvider";
import { useGetReportsQuery } from "../../../services/reportsApi";
import { REALTIME_INTERVAL_MINUTES, REALTIME_POLLING_MS, REALTIME_SLOTS } from "../utils/realtimeConfig";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { t } from "i18next";

interface DataPoint {
  timestamp: number;
  isLatest: boolean;
  timeLabel: string;
  label: string;
  index: number;
  totalClients: number | null;
  registeredUsers: number | null;
  guestUsers: number | null;
  registeredDevices: number | null;
}

export const RealtimeChart = () => {
  const { isConnected } = useRealtime();
  const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
  const INTERVAL_SECONDS = Math.max(1, Math.round(REALTIME_POLLING_MS / 1000));
  const [countdown, setCountdown] = useState(INTERVAL_SECONDS);
  const currentIndexRef = useRef(0);
  const tickIndexRef = useRef(0);
  const timePositions = Array.from({ length: REALTIME_SLOTS }, (_, i) => i * REALTIME_INTERVAL_MINUTES);
  const lastDataRef = useRef({
    totalClients: 0,
    registeredUsers: 0,
    guestUsers: 0,
    registeredDevices: 0,
  });

  const [visibleLines, setVisibleLines] = useState({
    totalClients: true,
    registeredUsers: true,
    guestUsers: true,
    registeredDevices: true,
  });

  const { data: realtimeStats, fulfilledTimeStamp } = useGetReportsQuery(undefined, {
    skip: !isConnected,
    pollingInterval: isConnected ? REALTIME_POLLING_MS : 0,
  });

  useEffect(() => {
    if (isConnected) {
      setCountdown(INTERVAL_SECONDS);
      currentIndexRef.current = 0;
      tickIndexRef.current = 0;
      setDataPoints([]);
      const baseline: DataPoint = {
        timestamp: Date.now(),
        isLatest: false,
        timeLabel: "0",
        label: "0",
        index: 0,
        totalClients: 0,
        registeredUsers: 0,
        guestUsers: 0,
        registeredDevices: 0,
      };
      const first: DataPoint = {
        timestamp: Date.now(),
        isLatest: true,
        timeLabel: String(REALTIME_INTERVAL_MINUTES),
        label: String(REALTIME_INTERVAL_MINUTES),
        index: 1,
        ...lastDataRef.current,
      };
      currentIndexRef.current = 2;
      tickIndexRef.current = 2;
      setDataPoints([baseline, first]);
    }
  }, [isConnected]);

  useEffect(() => {
    if (!isConnected) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? INTERVAL_SECONDS : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [isConnected]);

  const getCompleteChartData = () => {
    return Array.from({ length: REALTIME_SLOTS }, (_, idx) => {
      const existing = dataPoints.find((p) => p.index === idx);
      return (
        existing || {
          timestamp: 0,
          isLatest: false,
          timeLabel: String(idx * REALTIME_INTERVAL_MINUTES),
          label: String(idx * REALTIME_INTERVAL_MINUTES),
          index: idx,
          totalClients: undefined,
          registeredUsers: undefined,
          guestUsers: undefined,
          registeredDevices: undefined,
        }
      );
    });
  };

  useEffect(() => {
    if (!isConnected || !realtimeStats) return;
    lastDataRef.current = {
      totalClients: realtimeStats.realtime_active_total_clients || 0,
      registeredUsers: realtimeStats.realtime_active_registered_clients || 0,
      guestUsers: realtimeStats.realtime_active_guest_clients || 0,
      registeredDevices: realtimeStats.realtime_active_registered_users_sessions || 0,
    };
    setDataPoints((prev) => {
      if (prev.length === 0) return prev;
      const updated = prev.map((p) => ({ ...p, isLatest: false }));
      const last = updated[updated.length - 1];
      const upserted: DataPoint = {
        ...last,
        ...lastDataRef.current,
        isLatest: true,
        timestamp: Date.now(),
      };
      return [...updated.slice(0, -1), upserted];
    });
  }, [fulfilledTimeStamp]);

  useEffect(() => {
    if (!isConnected) return;
    const id = setInterval(() => {
      const newPoint: DataPoint = {
        timestamp: Date.now(),
        isLatest: true,
        timeLabel: String(tickIndexRef.current * REALTIME_INTERVAL_MINUTES),
        label: String(tickIndexRef.current * REALTIME_INTERVAL_MINUTES),
        index: tickIndexRef.current % REALTIME_SLOTS,
        ...lastDataRef.current,
      };
      setDataPoints((prev) => {
        const updated = prev.map((p) => ({ ...p, isLatest: false }));
        const sameIdx = updated.findIndex((p) => p.index === newPoint.index);
        if (sameIdx >= 0) {
          const replaced = [...updated];
          replaced[sameIdx] = newPoint;
          return replaced;
        }
        const appended = [...updated, newPoint];
        return appended.slice(-REALTIME_SLOTS);
      });
      tickIndexRef.current += 1;
      setCountdown(INTERVAL_SECONDS);
    }, REALTIME_POLLING_MS);
    return () => clearInterval(id);
  }, [isConnected, REALTIME_POLLING_MS]);

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center gap-4">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium"
          style={{ backgroundColor: "#1C67AD", color: "white" }}
        >
          {isConnected && (
            <span className="h-2 w-2 animate-pulse rounded-full" style={{ backgroundColor: "white" }}></span>
          )}
          {INTERVAL_SECONDS < 60
            ? `${t("realtimeOverview.lifeEvery")} ${INTERVAL_SECONDS} ${t("realtimeOverview.seconds")}`
            : `${t("realtimeOverview.lifeEvery")} ${REALTIME_INTERVAL_MINUTES} ${t("realtimeOverview.minutes")}`}
        </span>
        {isConnected && (
          <span
            className="inline-flex items-center rounded-full px-8 py-1 text-sm"
            style={{ backgroundColor: "rgba(28, 103, 173, 0.15)", marginLeft: "-40px" }}
          >
            {t("realtimeOverview.nextIn")} {formatCountdown(countdown)}
          </span>
        )}
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        <label className="flex cursor-pointer items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={visibleLines.totalClients}
            onChange={() => setVisibleLines((prev) => ({ ...prev, totalClients: !prev.totalClients }))}
            className="rounded"
          />
          <span className="h-3 w-3 rounded" style={{ backgroundColor: "#1C67AD" }}></span>
          <span>{t("realtimeOverview.clientsTotal")}</span>
        </label>
        <label className="flex cursor-pointer items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={visibleLines.registeredUsers}
            onChange={() => setVisibleLines((prev) => ({ ...prev, registeredUsers: !prev.registeredUsers }))}
            className="rounded"
          />
          <span className="h-3 w-3 rounded" style={{ backgroundColor: "#87C38E" }}></span>
          <span>{t("realtimeOverview.usersRegistered")}</span>
        </label>
        <label className="flex cursor-pointer items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={visibleLines.guestUsers}
            onChange={() => setVisibleLines((prev) => ({ ...prev, guestUsers: !prev.guestUsers }))}
            className="rounded"
          />
          <span className="h-3 w-3 rounded" style={{ backgroundColor: "#6b7280" }}></span>
          <span>{t("realtimeOverview.guestUsersGuests")}</span>
        </label>
        <label className="flex cursor-pointer items-center space-x-2 text-sm">
          <input
            type="checkbox"
            checked={visibleLines.registeredDevices}
            onChange={() => setVisibleLines((prev) => ({ ...prev, registeredDevices: !prev.registeredDevices }))}
            className="rounded"
          />
          <span className="h-3 w-3 rounded" style={{ backgroundColor: "#8b5cf6" }}></span>
          <span>{t("realtimeOverview.devicesRegistered")}</span>
        </label>
      </div>

      <div className="relative h-96">
        {!isConnected && (
          <div className="flex h-full items-center justify-center">
            <div className="text-lg text-gray-500">{t("realtimeOverview.connectFromHeaderToStartLiveMonitoring")}</div>
          </div>
        )}

        {isConnected && (
          <>
            {dataPoints.length === 0 && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
                <div className="text-center">
                  <div className="mx-auto mb-4 h-6 w-6 animate-pulse rounded-full bg-gray-300"></div>
                  <div className="text-sm text-gray-500">{t("realtimeOverview.waitingForData")}</div>
                </div>
              </div>
            )}
            {(() => {
              const chartData = getCompleteChartData();
              return (
                <ResponsiveContainer width="100%" height={350}>
                  <RechartsLineChart data={chartData} margin={{ top: 30, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                      formatter={(value, name) => {
                        const labels = {
                          totalClients: t("realtimeOverview.clientsTotal"),
                          registeredUsers: t("realtimeOverview.usersRegistered"),
                          guestUsers: t("realtimeOverview.guestUsersGuests"),
                          registeredDevices: t("realtimeOverview.devicesRegistered"),
                        };
                        return [value, labels[name as keyof typeof labels] || name];
                      }}
                      labelFormatter={(_, payload: any) => `Time: ${payload?.[0]?.payload?.timeLabel ?? ""}`}
                    />
                    <XAxis
                      dataKey="index"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#1C67AD", dy: 6 }}
                      tickMargin={8}
                      interval={0}
                      tickFormatter={(value: number) => {
                        const p = chartData.find((dp) => dp.index === value);
                        return p?.timeLabel ?? String(value);
                      }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#1C67AD" }}
                      domain={[0, (dataMax) => Math.max(dataMax + 10, dataMax < 5 ? 5 : 50)]}
                      allowDecimals={false}
                    />

                    {visibleLines.totalClients && (
                      <Line
                        type="monotone"
                        dataKey="totalClients"
                        stroke="#1C67AD"
                        strokeWidth={3}
                        connectNulls={true}
                        dot={({ cx, cy, payload }) => {
                          if (payload?.totalClients === undefined || payload?.totalClients === null) return <g />;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill="#1C67AD"
                              strokeWidth={2}
                              stroke="white"
                              style={{ cursor: "pointer" }}
                            />
                          );
                        }}
                      />
                    )}

                    {visibleLines.registeredUsers && (
                      <Line
                        type="monotone"
                        dataKey="registeredUsers"
                        stroke="#87C38E"
                        strokeWidth={3}
                        connectNulls={true}
                        dot={({ cx, cy, payload }) => {
                          if (payload?.registeredUsers === undefined || payload?.registeredUsers === null) return <g />;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={4}
                              fill="#87C38E"
                              strokeWidth={2}
                              stroke="white"
                              style={{ cursor: "pointer" }}
                            />
                          );
                        }}
                      />
                    )}

                    {visibleLines.guestUsers && (
                      <Line
                        type="monotone"
                        dataKey="guestUsers"
                        stroke="#6b7280"
                        strokeWidth={3}
                        connectNulls={true}
                        dot={({ cx, cy, payload }) => {
                          if (payload?.guestUsers === undefined || payload?.guestUsers === null) return <g />;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={4}
                              fill="#6b7280"
                              strokeWidth={2}
                              stroke="white"
                              style={{ cursor: "pointer" }}
                            />
                          );
                        }}
                      />
                    )}

                    {visibleLines.registeredDevices && (
                      <Line
                        type="monotone"
                        dataKey="registeredDevices"
                        stroke="#8b5cf6"
                        strokeWidth={3}
                        connectNulls={true}
                        dot={({ cx, cy, payload }) => {
                          if (payload?.registeredDevices === undefined || payload?.registeredDevices === null)
                            return <g />;
                          return (
                            <circle
                              cx={cx}
                              cy={cy}
                              r={4}
                              fill="#8b5cf6"
                              strokeWidth={2}
                              stroke="white"
                              style={{ cursor: "pointer" }}
                            />
                          );
                        }}
                      />
                    )}
                  </RechartsLineChart>
                </ResponsiveContainer>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};
